import { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import type { ModelActionChatMessage } from '@app/api/ActionsHistory';
import {
  getRecommendChatErrorMessage,
  isRecommendChatRetryableError,
  useAcknowledgeRecommendChatMessagesMutation,
} from '@app/api/RecommendChat';
import type { RecommendReadOperation } from '@app/api/RecommendChat/types';
import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { useUserPrint } from '@app/hooks';

import { compareRecommendRevisions } from '../lib/compareRecommendRevisions';
import {
  buildRecommendReadBatches,
  getRecommendMessageKey,
} from '../lib/recommendReadBatches';

import { useRecommendVisibleReadEffects } from './useRecommendVisibleReadEffects';

import type { RefObject } from 'react';

interface ReadAttempt {
  operation: RecommendReadOperation;
  status: 'sending' | 'pending' | 'retry' | 'rejected';
  revisions: Map<string, string>;
  resolvedIds: Set<string>;
}

interface UseRecommendVisibleReadParams {
  items: LeadCardHistoryItemData[];
  allItems: LeadCardHistoryItemData[];
  scrollContainerRef: RefObject<HTMLDivElement>;
  selectedLeadId: number | null;
  isEnabled: boolean;
  onReadAccepted(): void;
  refreshLatestHistory(timestamps?: string[]): Promise<void>;
}

export const useRecommendVisibleRead = ({
  items,
  allItems,
  scrollContainerRef,
  selectedLeadId,
  isEnabled,
  onReadAccepted,
  refreshLatestHistory,
}: UseRecommendVisibleReadParams) => {
  const {
    params: { ActiveBranchId, Database, UserId },
  } = useUserPrint();
  const [acknowledgeMessages] = useAcknowledgeRecommendChatMessagesMutation();
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const [readError, setReadError] = useState<string | null>(null);
  const scopeKey = `${Database}:${UserId}:${ActiveBranchId}:${selectedLeadId}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const enabledRef = useRef(isEnabled);
  enabledRef.current = isEnabled;
  const mountedRef = useRef(false);
  const nodesRef = useRef(new Map<string, HTMLDivElement>());
  const visibleRef = useRef(new Set<string>());
  const attemptsRef = useRef(new Map<string, ReadAttempt>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const flushRef = useRef<() => void>(() => undefined);

  const messages = useMemo(
    () => items.flatMap(({ ChatMessages }) => ChatMessages ?? []),
    [items]
  );
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const allMessages = useMemo(
    () => allItems.flatMap(({ ChatMessages }) => ChatMessages ?? []),
    [allItems]
  );
  const allMessagesRef = useRef(allMessages);
  allMessagesRef.current = allMessages;

  const mountHandler = useCallback(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const updatePendingHandler = useCallback(() => {
    const keys = new Set<string>();
    for (const {
      operation,
      status,
      resolvedIds,
    } of attemptsRef.current.values()) {
      if (status === 'rejected') continue;
      for (const messageId of operation.messageIds) {
        if (!resolvedIds.has(messageId)) {
          keys.add(getRecommendMessageKey(operation.conversationId, messageId));
        }
      }
    }
    setPendingKeys(keys);
  }, []);

  const getVisibleKeys = useCallback((): Set<string> => {
    if (
      !enabledRef.current ||
      !mountedRef.current ||
      document.visibilityState !== 'visible' ||
      !document.hasFocus()
    ) {
      return new Set();
    }
    const root = scrollContainerRef.current;
    if (!root || root.clientHeight <= 0 || root.clientWidth <= 0)
      return new Set();
    const rootBounds = root.getBoundingClientRect();
    const visible = new Set<string>();
    for (const key of visibleRef.current) {
      const node = nodesRef.current.get(key);
      if (!node || !node.isConnected) continue;
      const bounds = node.getBoundingClientRect();
      const left = Math.max(bounds.left, rootBounds.left, 0);
      const right = Math.min(bounds.right, rootBounds.right, window.innerWidth);
      const top = Math.max(bounds.top, rootBounds.top, 0);
      const bottom = Math.min(
        bounds.bottom,
        rootBounds.bottom,
        window.innerHeight
      );
      if (right <= left || bottom <= top) continue;
      // Intersection alone includes a chat covered by another modal. Verify
      // that a point inside the actually visible bubble belongs to this chat.
      const foreground = document.elementFromPoint(
        (left + right) / 2,
        (top + bottom) / 2
      );
      if (foreground && node.contains(foreground)) visible.add(key);
    }
    return visible;
  }, [scrollContainerRef]);

  const sendOperationHandler = useCallback(
    async (attempt: ReadAttempt) => {
      if (!ActiveBranchId || !Database || !UserId || !enabledRef.current)
        return;
      const visibleKeys = getVisibleKeys();
      if (
        !attempt.operation.messageIds.every((id) =>
          visibleKeys.has(
            getRecommendMessageKey(attempt.operation.conversationId, id)
          )
        )
      )
        return;
      const requestScope = scopeRef.current;
      attempt.status = 'sending';
      updatePendingHandler();
      try {
        const result = await acknowledgeMessages({
          ActiveBranchId,
          data: attempt.operation,
        }).unwrap();
        if (!mountedRef.current || scopeRef.current !== requestScope) return;
        if (
          result.operationId !== attempt.operation.operationId ||
          result.conversationId !== attempt.operation.conversationId ||
          result.messageIds.length !== attempt.operation.messageIds.length ||
          !attempt.operation.messageIds.every((id) =>
            result.messageIds.includes(id)
          )
        ) {
          attempt.status = 'retry';
          setReadError('Не удалось подтвердить прочтение. Повторите действие.');
          return;
        }
        attempt.status = 'pending';
        setReadError(null);
        onReadAccepted();
        void refreshLatestHistory(
          allMessagesRef.current
            .filter(
              (message) =>
                message.RecommendChat?.conversationId ===
                  attempt.operation.conversationId &&
                attempt.operation.messageIds.includes(
                  message.RecommendChat.messageId
                )
            )
            .map((message) => message.Timestamp)
        );
      } catch (error) {
        if (!mountedRef.current || scopeRef.current !== requestScope) return;
        attempt.status = isRecommendChatRetryableError(error)
          ? 'retry'
          : 'rejected';
        setReadError(
          getRecommendChatErrorMessage(
            error,
            'Не удалось подтвердить прочтение. Повторите действие.'
          )
        );
      } finally {
        if (mountedRef.current && scopeRef.current === requestScope)
          updatePendingHandler();
      }
    },
    [
      ActiveBranchId,
      Database,
      UserId,
      acknowledgeMessages,
      getVisibleKeys,
      onReadAccepted,
      refreshLatestHistory,
      updatePendingHandler,
    ]
  );

  const flushHandler = useCallback(() => {
    const visibleKeys = getVisibleKeys();
    if (!visibleKeys.size) return;
    const excludedKeys = new Set<string>();
    for (const attempt of attemptsRef.current.values()) {
      for (const messageId of attempt.operation.messageIds) {
        excludedKeys.add(
          getRecommendMessageKey(attempt.operation.conversationId, messageId)
        );
      }
      if (attempt.status === 'retry') void sendOperationHandler(attempt);
    }
    const operations = buildRecommendReadBatches(
      messagesRef.current,
      visibleKeys,
      excludedKeys,
      uuid
    );
    for (const operation of operations) {
      const revisions = new Map<string, string>();
      for (const message of messagesRef.current) {
        const metadata = message.RecommendChat;
        if (metadata?.conversationId === operation.conversationId) {
          revisions.set(metadata.messageId, metadata.revision);
        }
      }
      const attempt: ReadAttempt = {
        operation,
        status: 'retry',
        revisions,
        resolvedIds: new Set(),
      };
      attemptsRef.current.set(operation.operationId, attempt);
      void sendOperationHandler(attempt);
    }
  }, [getVisibleKeys, sendOperationHandler]);
  flushRef.current = flushHandler;

  const registerReadElementHandler = useCallback(
    (message: ModelActionChatMessage, node: HTMLDivElement | null) => {
      const metadata = message.RecommendChat;
      if (
        !metadata ||
        message.Direction !== 'in' ||
        metadata.origin !== 'client'
      )
        return;
      const key = getRecommendMessageKey(
        metadata.conversationId,
        metadata.messageId
      );
      const previous = nodesRef.current.get(key);
      if (previous) observerRef.current?.unobserve(previous);
      visibleRef.current.delete(key);
      if (node) {
        nodesRef.current.set(key, node);
        observerRef.current?.observe(node);
      } else {
        nodesRef.current.delete(key);
      }
    },
    []
  );

  const subscribeHandler = useCallback(() => {
    const root = scrollContainerRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') {
      return () => undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const key = Array.from(nodesRef.current).find(
            ([, node]) => node === entry.target
          )?.[0];
          if (!key) continue;
          if (entry.isIntersecting && entry.intersectionRatio > 0)
            visibleRef.current.add(key);
          else visibleRef.current.delete(key);
        }
        flushRef.current();
      },
      { root, threshold: [0, 0.01] }
    );
    observerRef.current = observer;
    nodesRef.current.forEach((node) => observer.observe(node));
    const activityHandler = () => flushRef.current();
    window.addEventListener('focus', activityHandler);
    document.addEventListener('visibilitychange', activityHandler);
    document.addEventListener('focusin', activityHandler);
    root.addEventListener('scroll', activityHandler);
    return () => {
      observer.disconnect();
      observerRef.current = null;
      window.removeEventListener('focus', activityHandler);
      document.removeEventListener('visibilitychange', activityHandler);
      document.removeEventListener('focusin', activityHandler);
      root.removeEventListener('scroll', activityHandler);
    };
  }, [scrollContainerRef]);

  const resetHandler = useCallback(() => {
    attemptsRef.current.clear();
    visibleRef.current.clear();
    setPendingKeys(new Set());
    setReadError(null);
  }, []);

  const reconcileHandler = useCallback(() => {
    for (const attempt of attemptsRef.current.values()) {
      for (const message of allMessages) {
        const metadata = message.RecommendChat;
        if (
          !metadata ||
          metadata.conversationId !== attempt.operation.conversationId ||
          !attempt.operation.messageIds.includes(metadata.messageId)
        )
          continue;
        const sameOperation =
          metadata.readOperationId === attempt.operation.operationId;
        const comparison = compareRecommendRevisions(
          metadata.revision,
          attempt.revisions.get(metadata.messageId) ?? '0'
        );
        if (
          (sameOperation && metadata.readOperationStatus === 'applied') ||
          (metadata.recipientState === 'read' &&
            comparison !== null &&
            comparison > 0)
        ) {
          attempt.resolvedIds.add(metadata.messageId);
        } else if (
          sameOperation &&
          metadata.readOperationStatus === 'rejected'
        ) {
          attempt.status = 'rejected';
          setReadError('Прочтение не подтверждено. Повторите действие.');
        }
      }
    }
    updatePendingHandler();
  }, [allMessages, updatePendingHandler]);

  const pollHandler = useCallback(() => {
    const pendingMessageKeys = new Set<string>();
    for (const attempt of attemptsRef.current.values()) {
      if (attempt.status === 'rejected') continue;
      for (const id of attempt.operation.messageIds) {
        if (!attempt.resolvedIds.has(id))
          pendingMessageKeys.add(
            getRecommendMessageKey(attempt.operation.conversationId, id)
          );
      }
    }
    void refreshLatestHistory(
      allMessagesRef.current
        .filter((message) => {
          const metadata = message.RecommendChat;
          return (
            metadata &&
            (metadata.staffReadPending ||
              pendingMessageKeys.has(
                getRecommendMessageKey(
                  metadata.conversationId,
                  metadata.messageId
                )
              ))
          );
        })
        .map((message) => message.Timestamp)
    );
    flushHandler();
  }, [flushHandler, refreshLatestHistory]);

  const retryReadHandler = useCallback(() => {
    for (const [id, attempt] of attemptsRef.current) {
      if (attempt.status === 'rejected') attemptsRef.current.delete(id);
    }
    setReadError(null);
    flushHandler();
  }, [flushHandler]);

  const hasPendingRead =
    pendingKeys.size > 0 ||
    allMessages.some((message) => message.RecommendChat?.staffReadPending);
  useRecommendVisibleReadEffects({
    scopeKey,
    isEnabled,
    hasPendingRead,
    onMount: mountHandler,
    onSubscribe: subscribeHandler,
    onReset: resetHandler,
    onReconcile: reconcileHandler,
    onPoll: pollHandler,
  });

  return {
    pendingKeys,
    readError,
    registerReadElementHandler,
    retryReadHandler,
  };
};
